/**
 * 平台故障与恢复这两屏的纯逻辑（W12 / W13）。
 *
 * 这两屏的共同点是：**它们出现的时刻，正是用户最想知道「到底怎么了」的时刻**，
 * 而最容易写出来的两句话恰好都没有信息量——「排队中」和「正在同步」。
 * 设计稿把它们都点名禁掉了，所以这里的函数产出的是**具体的事**，
 * 不是一个状态词。
 */
import type { SessionState } from './session-state'

/** W12 要说的四件事，顺序就是设计稿里的顺序。 */
export const HELD_LINES = Object.freeze([
  'lulu_workbench.held.not_your_fault',
  'lulu_workbench.held.production_fine',
  'lulu_workbench.held.dont_resubmit',
  'lulu_workbench.held.idempotent',
] as const)

/**
 * 「线上没受影响」这句必须在前三行。
 *
 * 写成一条可判定的断言而不是注释：设计稿说的是「用户真正怕的是线上出事」，
 * 而一句排在第七行的安慰，等于没说。
 */
export function productionLineIsEarly(): boolean {
  const i = HELD_LINES.indexOf('lulu_workbench.held.production_fine')
  return i >= 0 && i < 3
}

/** 断线期间发生的一件事。**必须具体到谁做了什么**。 */
export interface GapEvent {
  readonly who: string
  /** 做了什么。`null` 表示这个人这段时间没动——那也是一件要说的事。 */
  readonly what: string | null
}

/**
 * 一句要说的话。两种形态分开写而不是共用一个可选字段的对象：
 * 「他做了什么」与「他没动」是两句不同的话，合成一个带可选 what 的
 * 结构，渲染方就得自己判断走哪一句——那个判断迟早会写错一次。
 */
export type RecoveryLine =
  | {
      readonly copyId: 'lulu_workbench.recover.what_happened'
      readonly vars: { readonly who: string; readonly what: string }
    }
  | {
      readonly copyId: 'lulu_workbench.recover.idle'
      readonly vars: { readonly who: string }
    }

export interface RecoveryPlan {
  readonly count: number
  readonly lines: readonly RecoveryLine[]
}

/**
 * 把断线期间的事件铺成要说的几句话。
 *
 * 没动的人也要说（「Claude 等在那儿没动」）——设计稿点名要这一句。
 * 只说做了事的那些人，用户会以为其余的人不见了。
 */
export function planRecovery(events: readonly GapEvent[]): RecoveryPlan {
  return {
    count: events.length,
    lines: events.map((e) =>
      e.what === null
        ? { copyId: 'lulu_workbench.recover.idle' as const, vars: { who: e.who } }
        : {
            copyId: 'lulu_workbench.recover.what_happened' as const,
            vars: { who: e.who, what: e.what },
          },
    ),
  }
}

/**
 * 恢复没完成之前，界面上不许出现任何「进行中」。
 *
 * 这一条与断线守卫是同一族，但时机相反：断线那条管的是
 * 「连接断了要立刻撤掉运行态」，这条管的是**连接回来了、但事实还没补齐**
 * 那一段——那一段里我们同样不知道谁在跑，所以同样不能画成在跑。
 */
export function mayShowRunning(state: SessionState | null, recovered: boolean): boolean {
  if (state === 'recovering' && !recovered) return false
  return true
}
